import { Phone, Mail } from "lucide-react";
import { Eyebrow } from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import { SITE } from "@/constants/site";

const services = [
    {
        id: "tax",
        num: "01",
        title: "Tax Preparation & Planning",
        desc: "Personal and business returns, quarterly estimates, and a plan that starts long before filing season does.",
    },
    {
        id: "bookkeeping",
        num: "02",
        title: "Bookkeeping & Payroll",
        desc: "Clean monthly books and payroll that runs itself — so you always know exactly where you stand.",
    },
    {
        id: "advisory",
        num: "03",
        title: "Business Advisory & Startup Accounting",
        desc: "Entity setup, cash-flow visibility, and honest guidance for founders building from day one.",
    },
];

const values = [
    { num: "01", title: "Clarity", desc: "Plain-English numbers. No jargon, ever." },
    { num: "02", title: "Warmth", desc: "You're a person with a dream, not a file number." },
    { num: "03", title: "Precision", desc: "Detail-obsessed and deadline-true." },
    { num: "04", title: "Proactivity", desc: "We flag it before it becomes a problem." },
];

export default function Home() {
    return (
        <div data-testid="page-home">
            <section
                data-testid="hero-section"
                className="border-b border-ink/10 bg-bone"
            >
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
                    <Eyebrow>
                        {SITE.name} CPA · {SITE.location}
                    </Eyebrow>
                    <h1 className="mt-6 max-w-2xl font-serif text-5xl font-medium leading-[1.05] tracking-tight sm:text-6xl">
                        Accounting that{" "}
                        <span className="italic text-gold-deep">
                            feels human.
                        </span>
                    </h1>
                    <p className="mt-6 max-w-xl text-base leading-relaxed text-clay sm:text-lg">
                        Phillip CPA is a new practice for new and growing
                        businesses — tax, bookkeeping, payroll, and startup
                        advisory, delivered personally.
                    </p>
                    <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                        <a
                            href={SITE.phoneHref}
                            data-testid="hero-call-btn"
                            className="flex w-fit items-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-ink transition-colors hover:bg-gold-deep hover:text-cream"
                        >
                            <Phone className="h-4 w-4" />
                            Call {SITE.phoneDisplay}
                        </a>
                        <a
                            href={`mailto:${SITE.email}`}
                            data-testid="hero-email-btn"
                            className="flex w-fit items-center gap-2 rounded-full border border-ink/20 px-8 py-4 text-sm font-semibold text-ink transition-colors hover:border-gold hover:text-gold-deep"
                        >
                            <Mail className="h-4 w-4" />
                            {SITE.email}
                        </a>
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
                <Eyebrow>What we do</Eyebrow>
                <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
                    Everything a growing business needs. Nothing it doesn't.
                </h2>

                <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
                    {services.map((s) => (
                        <div
                            key={s.id}
                            data-testid={`service-card-${s.id}`}
                            className="flex h-full flex-col rounded-2xl border border-ink/10 bg-cream p-8 transition-colors hover:border-gold"
                        >
                            <p className="font-mono text-[11px] tracking-[0.25em] text-gold-deep">
                                {s.num}
                            </p>
                            <h3 className="mt-3 font-serif text-2xl font-medium">
                                {s.title}
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed text-clay">
                                {s.desc}
                            </p>
                        </div>
                    ))}
                </div>

                <div className="mt-10 rounded-2xl border border-ink/10 bg-bone p-8">
                    <p className="font-serif text-2xl font-medium">
                        Not sure where to start?{" "}
                        <span className="italic text-gold-deep">
                            Start with a conversation.
                        </span>
                    </p>
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                        <a
                            href={SITE.phoneHref}
                            data-testid="home-intro-call-btn"
                            className="flex w-fit items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-cream transition-colors hover:bg-gold-deep"
                        >
                            <Phone className="h-4 w-4" />
                            Book a free intro call
                        </a>
                        <p className="text-xs text-clay">
                            20 minutes · no jargon · no obligation
                        </p>
                    </div>
                </div>
            </section>

            <section className="border-y border-ink/10 bg-bone">
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
                    <Eyebrow>How we work</Eyebrow>
                    <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
                        {values.map((v) => (
                            <div
                                key={v.num}
                                className="border-t-2 border-ink/15 pt-5"
                                data-testid="home-value-item"
                            >
                                <p className="font-mono text-[11px] tracking-[0.25em] text-gold-deep">
                                    {v.num}
                                </p>
                                <h3 className="mt-2 font-serif text-xl font-medium">
                                    {v.title}
                                </h3>
                                <p className="mt-1 text-sm leading-relaxed text-clay">
                                    {v.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <CTABanner />
        </div>
    );
}
